import React, { useEffect, useState } from "react";
import { getWithTokenAPI } from "../../utils/Api";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  CircularProgress,
  Box,
  Typography,
  Pagination,
} from "@mui/material";
import { Email as EmailIcon } from "@mui/icons-material";
import GroupIcon from "@mui/icons-material/Group"; // Import GroupIcon for no data state consistency

const NewsletterPage = () => {
  const [newsletterData, setNewsletterData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalNewsletters, setTotalNewsletters] = useState(0); // State to store total count from API

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [newslettersPerPage] = useState(10); // Number of items per page

  // Colors
  const primaryGreen = "#DC3545";
  const lightGreen = "#ffe5e5";
  const secondaryTextColor = "rgba(0, 0, 0, 0.6)"; // Consistent with user table header text color

  // Fetch all newsletter subscriptions
  const fetchNewsletterSubscriptions = async (page, limit) => {
    const token = localStorage.getItem("token");
    setLoading(true);
    try {
      // IMPORTANT: Modified API call to include page and limit query parameters
      // This assumes your backend API can handle these parameters.
      const res = await getWithTokenAPI(
        token,
        `/newsletter/find-all?page=${page}&limit=${limit}`
      );

      if (res.statusCode === 200) {
        setNewsletterData(res.data.newsletters || []);
        setTotalNewsletters(res.data.totalCount || 0); // Assuming API returns totalCount
      } else {
        toast.error(res.message || "Failed to load newsletter subscriptions.");
        setNewsletterData([]);
        setTotalNewsletters(0);
      }
    } catch (error) {
      console.error("Fetch newsletter error:", error);
      toast.error(
        "Failed to load newsletter subscriptions due to a network error."
      );
      setNewsletterData([]);
      setTotalNewsletters(0);
    } finally {
      setLoading(false);
    }
  };

  // Pagination logic (now based on totalNewsletters from API)
  const totalPages = Math.ceil(totalNewsletters / newslettersPerPage);

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
    // When page changes, refetch data for the new page
    fetchNewsletterSubscriptions(value, newslettersPerPage);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Initial data fetch on component mount and when currentPage or newslettersPerPage changes
  useEffect(() => {
    fetchNewsletterSubscriptions(currentPage, newslettersPerPage);
  }, [currentPage, newslettersPerPage]); // Dependency array: refetch when page or items per page changes

  return (
    <Box sx={{ p: 3, width: "100%" }}>
      <ToastContainer position="top-center" autoClose={1500} />

      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          mb: 3,
          p: 2,
          backgroundColor: lightGreen,
          borderRadius: 2,
          borderLeft: `5px solid ${primaryGreen}`,
        }}
      >
        <EmailIcon sx={{ color: primaryGreen, fontSize: 50, mr: 2 }} />
        <Box>
          <Typography
            variant="h4"
            component="h1"
            sx={{ color: primaryGreen, fontWeight: "bold" }}
          >
            Newsletter Subscription
          </Typography>
          <Typography variant="body1" sx={{ color: "text.secondary" }}>
            Manage your newsletter subscribers
          </Typography>
        </Box>
      </Box>

      {/* Main Content - Updated Table Design */}
      {/* Newsletter Table */}
      <div className="table-container border rounded">
        <div className="table-responsive">
          {loading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "200px",
              }}
            >
              <CircularProgress style={{ color: "#DC3545" }} />
              <Typography sx={{ ml: 2, color: "#DC3545" }}>
                Loading newsletter subscriptions...
              </Typography>
            </Box>
          ) : (
            <table className="table m-0 resource-head">
              <thead
                className="sticky-header fs-6"
                style={{ backgroundColor: lightGreen }}
              >
                <tr>
                  <th
                    className="table-header"
                    style={{ color: secondaryTextColor, textAlign: "left", width: "33%" }}
                  >
                    S.No
                  </th>
                  <th
                    className="table-header"
                    style={{ color: secondaryTextColor, textAlign: "left", width: "34%" }}
                  >
                    Email
                  </th>
                  <th
                    className="table-header"
                    style={{ color: secondaryTextColor, textAlign: "left", width: "33%" }}
                  >
                    Subscription Date
                  </th>
                </tr>
              </thead>
              <tbody>
                {newsletterData.length > 0 ? (
                  newsletterData.map((item, index) => (
                    <tr key={item._id}>
                      <td style={{ textAlign: "left" }}>{(currentPage - 1) * newslettersPerPage + index + 1}</td>
                      <td style={{ textAlign: "left" }}>
                        <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                          {item.email || "N/A"}
                        </Typography>
                      </td>
                      <td style={{ textAlign: "left" }}>
                        {item.createdAt
                          ? new Date(item.createdAt).toLocaleDateString()
                          : "N/A"}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="3" className="text-center">
                      <GroupIcon
                        sx={{ fontSize: 60, color: "text.secondary" }}
                      />
                      <Typography variant="h6" sx={{ color: "text.secondary" }}>
                        No newsletter subscriptions found.
                      </Typography>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "end",
            mt: 3,
            pb: 2,
          }}
        >
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
            sx={{
              "& .MuiPaginationItem-root": {
                color: primaryGreen,
              },
              "& .MuiPaginationItem-root.Mui-selected": {
                backgroundColor: primaryGreen,
                color: "white",
              },
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default NewsletterPage;